import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
	SysAdminCompanyTestingWorkflowWithBatchTypeIds,
} from "node-library/dist/portal";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	FrontendCompanyWorkflow,
	FrontendCompanyWorkflowSteps,
} from "node-library/dist/structures/frontendTestWorkflowStructureDefinitions";
import { Severity } from "node-library/dist/constants";

import { Tab, Tabs } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { GreenButton } from "../../../../common-components/button";
import { CustomDrawer } from "../../../../common-components/drawers/drawer";
import UnsavedChangesModal from "../../../../common-components/modals/unsavedChangesModal";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { buildStepTree } from "../../../../helpers/workflowHelper";
import { sendGETRequest } from "../../../../requestHelper/sendRequest";
import { WorkflowStepItem } from "../../batchDetailsPage/components/workflowComponents/workflowStepItem";
import WorkflowCell from "./workflowCell";
import WorkflowStepTab from "./workflowStepTab";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;

	workflow: SysAdminCompanyTestingWorkflowWithBatchTypeIds;
	batchTypes: Array<string>;
}

export default function WorkflowStructureDetailsDrawer(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { company, setCompany, user, batchTypes, workflow, open, setOpen } =
		props;

	const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
		useState<boolean>(false);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);

	const [workflowDetails, setWorkflowDetails] =
		useState<FrontendCompanyWorkflow>();

	const [selectedTab, setSelectedTab] = useState<number>(0);

	const [renderingStepId, setRenderingStepId] = useState<string>("");

	const [loading, setLoading] = useState<boolean>(false);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setSelectedTab(newValue);
	};

	async function getWorkflowDetails() {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/structures/get-workflow-structure",
				{ workflowId: workflow.workflowId },
			);
			const workflowDetails: FrontendCompanyWorkflow = response?.data?.workflow;
			setWorkflowDetails(workflowDetails);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}
	useEffect(() => {
		getWorkflowDetails();
	}, [workflow]);

	//STEPS TAB

	const workflowStepsTab: React.ReactNode = workflowDetails ? (
		<WorkflowStepTab
			company={company}
			setCompany={setCompany}
			user={user}
			workflowDetails={workflowDetails}
			setWorkflowDetails={setWorkflowDetails}
		/>
	) : (
		<></>
	);
	const workflowTransitionTab: React.ReactNode = <></>;

	let content: React.ReactNode = (
		<div>
			<div>
				<WorkflowCell
					key={workflow.workflowId} // Always use a unique key
					workflow={workflow}
					batchTypes={batchTypes}
					goToViewWorkflowDetails={() => {}}
				/>
				<div style={{ display: "flex", justifyContent: "center" }}>
					<GreenButton
						id={"spec-finish-button"}
						size="small"
						onClick={() => {
							setDrawerOpen(false);
						}}
					>
						{translation("Finish")}
					</GreenButton>
				</div>
			</div>

			<div className="">
				<Tabs
					value={selectedTab}
					onChange={handleChange}
					aria-label="Structures"
					variant="scrollable"
					scrollButtons="auto"
					sx={{
						"& .MuiTab-root": {
							cursor: "pointer",
						},
					}}
				>
					<Tab label={translation("Steps")} />
					<Tab label={translation("Transitions")} />
				</Tabs>
				<div style={{ marginTop: "10px" }}>
					{selectedTab === 0 && workflowStepsTab}
					{selectedTab === 1 && workflowTransitionTab}
				</div>
			</div>
		</div>
	);

	function setDrawerOpen(drawerOpen: boolean) {
		if (!drawerOpen && unsavedChanges) {
			setUnsavedChangesModalOpen(true);
		} else {
			setOpen(drawerOpen);
		}
	}
	const workflowDetailsDrawer: React.ReactNode = (
		<CustomDrawer
			anchor={"right"}
			open={open}
			setOpen={setDrawerOpen}
			headerText={translation("ManageWorkflow")}
			headerDescription={translation("ManageWorkflowDescription")}
			drawerWidth={`100%`}
		>
			{content}
		</CustomDrawer>
	);

	return (
		<>
			{workflowDetailsDrawer}
			<UnsavedChangesModal
				open={unsavedChangesModalOpen}
				setOpen={setUnsavedChangesModalOpen}
				setUnsavedChanges={setUnsavedChanges}
				proceedAction={() => {
					setUnsavedChanges(false);
					setOpen(false);
				}}
			></UnsavedChangesModal>
		</>
	);
}
